import { request } from '@/api/httpClient'
import { ContractType, RequestType } from '@/enums'

export async function contractSendTradeCustomerQuoteReadyEmail(contractId, jobKey) {
  return request({
    method: 'contract/SendTradeCustomerQuoteReadyEmail',
    payload: {
      contractId,
      jobKey
    }
  })
}

export async function contractList(
  type,
  limit,
  offset,
  searchForReference,
  statusId,
  customerId,
  enquirySource,
  onlyShowLocked
) {
  const options = {
    type,
    limit: parseInt(limit, 10),
    offset: parseInt(offset, 10),
    searchForReference,
    GetTotal: true,
    CustomerId: customerId,
    EnquirySource: enquirySource,
    OnlyShowLocked: onlyShowLocked
  }

  if (type === ContractType.QUOTE && statusId !== '') {
    options.QuoteStatus = Number(statusId)
  }

  if (type === ContractType.ORDER && statusId !== '') {
    options.OrderStatus = Number(statusId)
  }

  if (type === ContractType.ENQUIRY && statusId !== '') {
    options.EnquiryStatus = Number(statusId)
  }

  return request({
    method: 'contract/List',
    payload: options
  })
}

export async function contractSummary({
  type,
  limit,
  offset,
  searchForReference,
  statusId,
  customerId,
  enquirySource,
  onlyShowLocked,
  enquiryStatus,
  IncludeItems,
  customQuoteStageId,
  fromDate,
  toDate,
  branchCompanyId,
  showExpired
}) {
  const options = {
    type,
    limit: parseInt(limit, 10),
    offset: parseInt(offset, 10),
    searchForReference,
    GetTotal: true,
    CustomerId: customerId,
    CustomQuoteStageId: customQuoteStageId,
    EnquirySource: enquirySource,
    OnlyShowLocked: onlyShowLocked,
    enquiryStatus,
    IncludeItems,
    FromDate: fromDate,
    ToDate: toDate,
    BranchCompanyId: branchCompanyId,
    ShowExpired: showExpired
  }

  if (type === ContractType.QUOTE && statusId) {
    options.QuoteStatus = Number(statusId)
  }

  if (type === ContractType.ORDER && statusId) {
    options.OrderStatus = Number(statusId)
  }

  if (type === ContractType.ENQUIRY && statusId) {
    if (Array.isArray(statusId)) {
      options.EnquiryStatus = statusId
    } else {
      options.EnquiryStatus = [Number(statusId)]
    }
  }

  return request({
    method: 'contract/Summary',
    payload: options
  })
}

export async function contractPortalSummary({
  contractType,
  customerId,
  limit,
  offset,
  searchForReference,
  fromDate,
  toDate
}) {
  return request({
    method: 'contract/PortalSummary',
    payload: {
      contractType,
      customerId,
      limit,
      offset,
      searchForReference,
      fromDate,
      toDate
    }
  })
}

export async function contractExtruderSummary({
  contractType,
  customerId,
  limit,
  offset,
  searchForReference,
  fromDate,
  toDate
}) {
  return request({
    method: 'contract/ExtruderSummary',
    payload: {
      contractType,
      customerId,
      limit,
      offset,
      searchForReference,
      fromDate,
      toDate
    }
  })
}

export async function contractPortalEnquiriesSummary({
  limit,
  offset,
  searchForReference,
  fromDate,
  toDate,
  enquiryStatus
}) {
  return request({
    method: 'contract/PortalEnquiriesSummary',
    payload: {
      limit,
      offset,
      searchForReference,
      fromDate,
      toDate,
      enquiryStatus
    }
  })
}

export async function contractAdminEnquiriesSummary({
  limit,
  offset,
  searchForReference,
  fromDate,
  toDate,
  enquiryStatus
}) {
  return request({
    method: 'contract/AdminEnquiriesSummary',
    payload: {
      limit,
      offset,
      searchForReference,
      fromDate,
      toDate,
      enquiryStatus
    }
  })
}

export async function contractAdminSummary(payload) {
  return request({
    method: 'contract/AdminSummary',
    payload: payload,
    signal: payload.signal
  })
}

export async function contractEnquiriesDashboardSummary({ limit, offset, enquirySource, enquiryStatus }) {
  return request({
    method: 'contract/enquiriesDashboardSummary',
    payload: {
      limit,
      offset,
      enquiryStatus,
      enquirySource
    }
  })
}

export async function contractExtruderDashboardSummary({ type, limit, offset }) {
  return request({
    method: 'contract/extruderDashboardSummary',
    payload: {
      type,
      limit,
      offset
    }
  })
}

export async function contractListSaveStates({ type, customerId, OnlyTheseContracts }) {
  return request({
    method: 'contract/ListSaveStates',
    payload: {
      type,
      customerId,
      OnlyTheseContracts
    }
  })
}

export async function contractConvertToOrder(contractId, jobIds, customerId = undefined) {
  return request({
    method: 'contract/ConvertToOrder',
    payload: {
      ContractId: contractId,
      JobKeys: jobIds,
      CustomerId: customerId
    }
  })
}

export async function contractConvertToQuote(contractId, jobIds) {
  const response = await request({
    method: 'contract/ConvertToQuote',
    payload: {
      ContractId: Number(contractId),
      JobKeys: jobIds.map((jobId) => Number(jobId))
    }
  })
  return response.contractId
}

export async function contractListJobs(contractId, jobKey, processingLevel, includeItems = true) {
  const { jobs } = await request({
    method: 'contract/ListJobs',
    payload: {
      ContractId: parseInt(contractId, 10)
    }
  })

  return Promise.all(
    jobs.map(async (job) => {
      if (jobKey !== undefined && job.jobKey !== jobKey) {
        return {
          ...job,
          items: []
        }
      }

      const fullJob = await contractJobDetails(contractId, job.jobKey)

      if (!includeItems) {
        return fullJob
      }

      const formattedItems = await Promise.all(
        fullJob.items.map(async (item) => {
          const fullItem = await contractJobLineItemDetails(
            contractId,
            job.jobKey,
            item.key,
            processingLevel,
            fullJob.exchangeRate
          )

          return {
            ...item,
            ...fullItem
          }
        })
      )

      return {
        ...job,
        ...fullJob,
        items: formattedItems
      }
    })
  )
}

export async function contractUpdateNote(params) {
  return request({
    method: 'contract/UpdateNote',
    payload: {
      ...params
    }
  })
}

export async function contractGetNotes(params) {
  return request({
    method: 'contract/GetNotes',
    payload: {
      ...params,
      ContractId: Number(params.ContractId)
    }
  })
}

export async function contractJobDetails(contractId, jobKey) {
  const response = await request({
    method: 'contract/JobDetails',
    payload: {
      ContractId: parseInt(contractId, 10),
      JobKey: parseInt(jobKey, 10)
    }
  })

  if (response.exchangeRate === null || response.exchangeRate === 0) {
    response.exchangeRate = 1
  }

  return response
}

export async function contractDetails(customerId, contractId, jobId = 1) {
  const params = {
    ContractIds: [Number(contractId)]
  }

  if (window.VITE_APP_INSTALLATION_TYPE === 'business') {
    params.CustomerId = customerId
  }
  const contract = await request({
    method: 'contract/Details',
    payload: params
  })
  const fullJob = await contractJobDetails(contractId, jobId)
  fullJob.currency = contract[0].currencyCode
  fullJob.customerCurrency = contract[0].customerCurrencyCode
  return {
    ...contract[0],
    jobs: [fullJob]
  }
}

export async function contractJobLineItemDetails(contractId, jobKey, itemKey, processingLevel, exchangeRate) {
  const response = await request({
    method: 'contract/JobLineItemDetails',
    payload: {
      ContractId: parseInt(contractId, 10),
      JobKey: jobKey,
      ItemKey: parseInt(itemKey, 10),
      ProcessingLevel: processingLevel
    }
  })

  response.calculatedPrice *= exchangeRate
  response.price *= exchangeRate

  return response
}

export async function contractUpdateJobRequote({ contractId, jobKey, requotePriceBeforeTax }) {
  return request({
    method: 'contract/UpdateJobRequote',
    payload: {
      ContractId: contractId,
      JobKey: jobKey,
      RequotePriceBeforeTax: requotePriceBeforeTax
    }
  })
}

export async function contractSendRequoteEmailToConsumer(contractId, jobKey) {
  return request({
    method: 'contract/SendRequoteEmailToConsumer',
    payload: {
      ContractId: contractId,
      JobKey: jobKey
    }
  })
}

export async function contractUpdateVisibility(contractId, { visibleToAdmin, visibleToPortal }) {
  return request({
    method: 'contract/UpdateVisibility',
    payload: {
      ContractId: contractId,
      VisibleToPortal: visibleToPortal,
      VisibleToAdmin: visibleToAdmin
    }
  })
}

export async function contractCancelOrder(contractId) {
  return request({
    method: 'contract/CancelOrder',
    payload: {
      ContractId: contractId
    }
  })
}

export async function contractUpdateConsumer({
  contractId,
  customerId,
  salutation,
  firstName,
  lastName,
  email,
  mobile,
  addressLine1,
  addressLine2,
  addressLine3,
  addressTown,
  addressCounty,
  addressCode,
  addressIso
}) {
  return request({
    method: 'contract/UpdateConsumer',
    payload: {
      ContractId: parseInt(contractId, 10),
      CustomerId: customerId,
      Salutation: salutation,
      FirstName: firstName,
      LastName: lastName,
      Email: email,
      Mobile: mobile,
      AddressLine1: addressLine1,
      AddressLine2: addressLine2,
      AddressLine3: addressLine3,
      AddressTown: addressTown,
      AddressCounty: addressCounty,
      AddressCode: addressCode,
      AddressIso: addressIso
    }
  })
}

export async function contractAssignCustomQuoteStage(ContractId, CustomerId, CustomQuoteStageId) {
  return request({
    method: 'contract/AssignCustomQuoteStage',
    payload: {
      ContractId,
      CustomerId,
      CustomQuoteStageId
    }
  })
}

export async function contractAddStaffToContract(ContractId, JobKey, Role, StaffId) {
  return request({
    method: 'contract/AddStaffToContract',
    payload: {
      ContractId,
      JobKey,
      Role,
      StaffId
    }
  })
}

export async function contractGetTotalDealerEnquiries() {
  return request({
    method: 'contract/GetTotalDealerEnquiries'
  })
}

export async function contractGetDownloadHistory(contractId) {
  return request({
    method: 'contract/GetDownloadHistory',
    payload: { contractId }
  })
}

export async function contractUpdateFabricatorJobPriceOverride(payload) {
  return request({
    method: 'contract/UpdateFabricatorJobPriceOverride',
    payload
  })
}

export async function contractSaveStatus(contractId) {
  return request({
    method: `contract/saveStatus?contractId=${contractId}`,
    requestType: RequestType.GET
  })
}

export async function contractUpdatePaymentStatus(ContractId, PaymentStatus) {
  return request({
    method: 'contract/UpdatePaymentStatus',
    payload: {
      ContractId,
      PaymentStatus
    }
  })
}
